/**
 * What Jev hides from rows and browse, or takes off the site (utils/not-recommended.ts):
 * the seed list, edited on /dash/search > Hidden items and stored with the
 * categories and tags (utils/nsfw-list.ts). No imports, so the dash and
 * nsfw-list-rules.ts can read it without pulling the Jev SDK or the ES client
 * into the browser bundle.
 *
 * Two actions (owner, 2026-10-28: "they aren't just nsfw, they're not allowed
 * to be sold"):
 *   hide    live, but out of every row, For You and browse page; typed search finds it.
 *   remove  not allowed on Sellwild (data/js/prohibited-items.ts): archived, held
 *           for review on /dash/reports, and listed in the admin daily digest.
 *           Seller and Vendoo listings only; feeds and partners are vetted.
 */

export type JevKindAction = 'hide' | 'remove';
export const JEV_KIND_ACTIONS: readonly JevKindAction[] = ['hide', 'remove'];

/** One thing Jev looks for: `kind` is what a judged row stores (VARCHAR(16)). */
export interface JevKind {
  kind: string;
  label: string;
  /** What Jev reads when it picks: the more look-alikes it names, the better. */
  description: string;
  /** Absent on lists saved before 2026-10-28: hide. */
  action?: JevKindAction;
}

/** What Jev does with a listing of this kind. */
export function actionOf(kind: JevKind): JevKindAction {
  return kind.action === 'remove' ? 'remove' : 'hide';
}

/** The kinds Jev may pick that are shown. Fixed: they tell the look-alikes apart. */
export const SHOWN_KINDS = ['sleepwear', 'clothing', 'other'] as const;

/** A stored kind: lowercase, starts with a letter, fits the 16-character column. */
export const KIND_PATTERN = /^[a-z][a-z0-9-]{1,15}$/;

/** The stored kind for a name typed on the dash, e.g. "Gambling gear" -> "gambling-gear". */
export function kindFromLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 16)
    .replace(/-+$/, '');
}

/**
 * The list until one is saved on the dash (owner's picks, 2026-10-28). A
 * `remove` kind's id is its rule id on data/js/prohibited-items.ts.
 */
export const JEV_KINDS_SEED: JevKind[] = [
  {
    kind: 'underwear',
    label: 'Underwear and lingerie',
    action: 'hide',
    description:
      'Underwear and lingerie: bras of any kind (sports bras, bralettes), panties, thongs, briefs, boxers, boxer briefs, ' +
      'shapewear, corsets and bustiers worn as lingerie, garters, negligees, chemises, lingerie teddies, sheer or sexy nightwear, pasties',
  },
  {
    kind: 'sexual',
    label: 'Sexy items and imagery',
    action: 'hide',
    description:
      'Sexy but not explicit: sex toys, condoms, lubricant, fetish gear, sexy costumes and role-play outfits, and any art, ' +
      'photography, photobook, magazine, comic, manga, poster, print, postcard, photo or trading card whose pictures are of ' +
      "people posed in swimsuits, lingerie or partly nude: men's magazines, pin-ups, and gravure and idol photobooks, " +
      'magazines, videos and cards',
  },
  { kind: 'swimwear', label: 'Swimwear', action: 'hide', description: 'Swimwear: swimsuits, bikinis, swim trunks, rash guards, cover-ups' },
  {
    kind: 'hosiery',
    label: 'Hosiery and legwear',
    action: 'hide',
    description: 'Hosiery and legwear: pantyhose, nylons, stockings, thigh highs, fishnets, sheer or opaque tights, garter stockings',
  },
  {
    kind: 'violence',
    label: 'Violence and abuse',
    action: 'hide',
    description: 'Violence and abuse: gore, graphic violence, real crime, death or torture images, abuse imagery, serial-killer memorabilia',
  },
  {
    kind: 'adult',
    label: 'Explicit adult content',
    action: 'remove',
    description:
      'Sexually explicit material: pornographic movies, magazines, photos and comics, hentai, and nude or erotic images made to arouse',
  },
  {
    kind: 'weapons',
    label: 'Weapons and ammunition',
    action: 'remove',
    description:
      'Weapons: firearms, gun parts and accessories, ammunition, holsters, explosives, fixed-blade, combat, tactical and ' +
      'hunting knives, folding and pocket knives, switchblades, daggers, swords, machetes, bayonets, brass knuckles, ' +
      'crossbows, stun guns, tasers, pepper spray, realistic airsoft and BB guns',
  },
  {
    kind: 'drugs',
    label: 'Drugs and drug gear',
    action: 'remove',
    description:
      'Drugs and drug gear: illegal drugs, bongs, smoking pipes, dab rigs, rolling papers, herb grinders, vapes and ' +
      'e-cigarettes, drug scales, and items that celebrate drug use',
  },
  {
    kind: 'prescription',
    label: 'Prescription medicine',
    action: 'remove',
    description: 'Prescription medicine: any drug or medicine that needs a prescription, including pills, injections and inhalers',
  },
  {
    kind: 'alcohol-tobacco',
    label: 'Alcohol and tobacco',
    action: 'remove',
    description:
      'Alcohol and tobacco for use: beer, wine, liquor, cigarettes, cigars and loose tobacco. Not empty bottles or ' +
      'collectible packaging',
  },
  {
    kind: 'animals',
    label: 'Live animals',
    action: 'remove',
    description: 'Live animals for sale: pets, livestock, fish, birds or reptiles. Not pet supplies, toys or figurines',
  },
  {
    kind: 'hazardous',
    label: 'Recalled or dangerous goods',
    action: 'remove',
    description:
      'Recalled or dangerous goods: products under a safety recall, fireworks, fuel, pressurized tanks, and other hazardous materials',
  },
  {
    kind: 'counterfeit-ids',
    label: 'Fake money and IDs',
    action: 'remove',
    description: "Fake money and IDs: counterfeit currency, and fake IDs, passports or driver's licenses",
  },
  {
    kind: 'hate',
    label: 'Hate symbols',
    action: 'remove',
    description:
      'Hate symbols: Nazi and SS items, swastikas, KKK items, Confederate flags, white-power or racist slogans and imagery, ' +
      'racist caricatures',
  },
];
